import { X, SlidersHorizontal, RotateCcw } from "lucide-react";

const STATUSES = [
  { value: "active", label: "Active" },
  { value: "live_client", label: "Live Client" },
  { value: "referral", label: "Referral" },
];

const CONTACT_SOURCES = [
  "Website", "Referral", "Software Finder", "Conference",
  "Gartner", "Software Advice", "Ampliz", "Cold Call", "Direct",
];

const INDUSTRIES = ["Healthcare", "Technology", "Finance", "Other"];

const PRODUCTS = [
  "AI-powered EHR (MDNet)", "Practice Management", "Medical Billing / RCM",
  "RevQ AI Billing", "CCM", "RPM", "APCM", "AWV Program",
  "REACH / Patient Engagement", "Telemedicine", "EPCS", "AI Scribe Only",
];

const ROLES = ["Owner", "CEO", "Doctor", "Practice Manager", "Office Manager", "CFO", "IT Director", "NP/PA", "Other"];

function FilterSection({ label, options, selected, onToggle, testPrefix }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</label>
        {selected.length > 0 && <span style={{ fontSize: 9, color: "var(--blue)", fontWeight: 600 }}>{selected.length}</span>}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {options.map(opt => {
          const val = typeof opt === "object" ? opt.value : opt;
          const lbl = typeof opt === "object" ? opt.label : opt;
          const isSelected = selected.includes(val);
          return (
            <button key={val} onClick={() => onToggle(val)}
              data-testid={`filter-${testPrefix}-${val.replace(/[\s\/()$+]+/g, '-').toLowerCase()}`}
              style={{
                padding: "4px 10px", fontSize: 11, borderRadius: 6,
                border: `0.5px solid ${isSelected ? "var(--blue)" : "var(--border)"}`,
                background: isSelected ? "var(--blue-light)" : "transparent",
                color: isSelected ? "var(--blue)" : "var(--text-secondary)",
                cursor: "pointer", fontWeight: isSelected ? 600 : 400,
                fontFamily: "var(--font)", transition: "all 0.15s",
              }}>
              {lbl}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export const EMPTY_CONTACT_FILTERS = {
  statuses: [], sources: [], industries: [], products: [], roles: [],
};

export function countActiveContactFilters(filters) {
  return Object.values(filters).reduce((sum, arr) => sum + arr.length, 0);
}

export function applyContactFilters(contacts, leads, filters) {
  return contacts.filter(contact => {
    if (filters.statuses.length > 0) {
      if (!filters.statuses.includes(contact.status)) return false;
    }
    if (filters.sources.length > 0) {
      const src = (contact.source || "").toLowerCase();
      if (!filters.sources.some(f => src.includes(f.toLowerCase()))) return false;
    }
    if (filters.industries.length > 0) {
      if (!filters.industries.includes(contact.industry)) return false;
    }
    if (filters.products.length > 0) {
      const contactLeads = leads.filter(l =>
        l.contact_id === contact.id || (l.email && l.email === contact.email)
      );
      const prods = [...new Set(contactLeads.flatMap(l => l.product_interest || []))];
      const tags = contact.tags || [];
      const allProds = [...prods, ...tags].map(p => p.toLowerCase());
      if (!filters.products.some(f => allProds.some(p => p.includes(f.toLowerCase()) || f.toLowerCase().includes(p)))) return false;
    }
    if (filters.roles.length > 0) {
      const title = (contact.job_title || "").toLowerCase();
      if (!filters.roles.some(f => title.includes(f.toLowerCase()))) return false;
    }
    return true;
  });
}

export default function ContactFilterPanel({ filters, onChange, onClose }) {
  const activeCount = countActiveContactFilters(filters);

  const toggle = (field, value) => {
    const arr = filters[field] || [];
    onChange({ ...filters, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] });
  };

  const clearAll = () => onChange({ ...EMPTY_CONTACT_FILTERS });

  return (
    <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 1000 }} data-testid="contact-filter-panel">
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.15)" }} onClick={onClose} />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: 380,
        background: "var(--surface)", borderLeft: "0.5px solid var(--border)",
        display: "flex", flexDirection: "column",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.08)",
        animation: "slideInRight 0.2s ease-out",
      }}>
        <div style={{ padding: "16px 20px", borderBottom: "0.5px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SlidersHorizontal style={{ width: 16, height: 16, color: "var(--blue)" }} />
            <span style={{ fontSize: 14, fontWeight: 650 }}>Filters</span>
            {activeCount > 0 && <span className="tag tag-blue" style={{ fontSize: 9 }}>{activeCount} active</span>}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {activeCount > 0 && (
              <button className="btn" onClick={clearAll} style={{ fontSize: 10, color: "var(--red)" }} data-testid="clear-contact-filters-btn">
                <RotateCcw style={{ width: 11, height: 11 }} /> Clear all
              </button>
            )}
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="close-contact-filters-btn">
              <X style={{ width: 16, height: 16, color: "var(--text-tertiary)" }} />
            </button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          <FilterSection label="Status" options={STATUSES} selected={filters.statuses} onToggle={v => toggle("statuses", v)} testPrefix="status" />
          <FilterSection label="Source" options={CONTACT_SOURCES} selected={filters.sources} onToggle={v => toggle("sources", v)} testPrefix="csource" />
          <FilterSection label="Role / Job Title" options={ROLES} selected={filters.roles} onToggle={v => toggle("roles", v)} testPrefix="role" />
          <FilterSection label="Products / Tags" options={PRODUCTS} selected={filters.products} onToggle={v => toggle("products", v)} testPrefix="cprod" />
          <FilterSection label="Industry" options={INDUSTRIES} selected={filters.industries} onToggle={v => toggle("industries", v)} testPrefix="ind" />
        </div>
      </div>
    </div>
  );
}
